import * as Sentry from '@sentry/angular';
import { ServerSyncDataInterface } from '@shared/api';
import { environment, EnvironmentLocalesEnum } from '@shared/environments';
import { isEqual, isNil } from 'lodash-es';
import { UserAppDataInterface } from './user-data.interface';
import { initialState, USER_DATA_SCHEMA_VERSION } from './user-data.store';

interface UserDataValidator {
  id: UserDataValidatorEnum;
  title: string;
  schemaVersion: SchemaVersionType;
  validate: (
    data: ServerSyncDataInterface<UserAppDataInterface>,
  ) => ServerSyncDataInterface<UserAppDataInterface>;
}

type SchemaVersionType = typeof USER_DATA_SCHEMA_VERSION;

enum UserDataValidatorEnum {
  VALIDATE_ALL_FIELDS_ARE_PRESENT = 'VALIDATE_ALL_FIELDS_ARE_PRESENT',
  VALIDATE_ALL_TABLES_HAVE_CORRECT_STRUCTURE = 'VALIDATE_ALL_TABLES_HAVE_CORRECT_STRUCTURE',
  // TODO: Remove when multiple locales are supported
  VALIDATE_CORRECT_LOCALE = 'VALIDATE_CORRECT_LOCALE',
}

export const validateUserData = (
  data: ServerSyncDataInterface<UserAppDataInterface>,
): ServerSyncDataInterface<Required<UserAppDataInterface>> => {
  let validatedData = structuredClone(data);

  for (const validator of validationOrder) {
    try {
      validatedData = validator.validate(validatedData);
    } catch (error) {
      console.error(
        `Something went wrong during the validation process with the ${validator.id} validator.\n${error}`,
      );
      Sentry.captureException(error);
      validatedData = resetUserData(data);
      break;
    }
  }

  return validatedData as ServerSyncDataInterface<
    Required<UserAppDataInterface>
  >;
};

export const resetUserData = (
  data: ServerSyncDataInterface<UserAppDataInterface>,
): ServerSyncDataInterface<Required<UserAppDataInterface>> => ({
  ...data,
  appData: initialState.appData,
});

const validators: Record<UserDataValidatorEnum, UserDataValidator> = {
  [UserDataValidatorEnum.VALIDATE_ALL_FIELDS_ARE_PRESENT]: {
    id: UserDataValidatorEnum.VALIDATE_ALL_FIELDS_ARE_PRESENT,
    title: 'Validate if all required fields are present and restore not ones',
    schemaVersion: 16, //When increment make sure this validator's logic is still relevant
    validate: (data) => {
      const dataKeys = Object.keys(data.appData);
      const initialDataKeys = Object.keys(initialState.appData);

      if (
        data.appData.schemaVersion !== USER_DATA_SCHEMA_VERSION ||
        initialDataKeys.some((key) => !dataKeys.includes(key)) ||
        dataKeys.some((key) => !initialDataKeys.includes(key))
      ) {
        return resetUserData(data);
      }

      return data;
    },
  },
  [UserDataValidatorEnum.VALIDATE_ALL_TABLES_HAVE_CORRECT_STRUCTURE]: {
    id: UserDataValidatorEnum.VALIDATE_ALL_TABLES_HAVE_CORRECT_STRUCTURE,
    title: 'Validate if all tables have correct structure',
    schemaVersion: 16, //When increment make sure this validator's logic is still relevant
    validate: (data) => {
      const validatedData = structuredClone(data);

      const tableIds = Object.keys(validatedData.appData.table ?? {});

      for (const tableId of tableIds) {
        const table = validatedData.appData.table?.[tableId];

        if (table) {
          if (table.columns) {
            if (
              isNil(table.columns.ids) ||
              isNil(table.columns.entities) ||
              table.columns.ids.some((id) =>
                isNil(table.columns?.entities[id]),
              ) ||
              table.columns.ids.some(
                (colId) =>
                  !isEqual(
                    Object.keys(table.columns?.entities?.[colId] ?? {}),
                    ['hide', 'width', 'pinned'],
                  ),
              )
            ) {
              delete validatedData.appData.table?.[tableId]?.columns;
              continue;
            }
          }

          if (table.sort) {
            if (isNil(table.sort.colId)) {
              delete validatedData.appData.table?.[tableId]?.sort;
            }
          }
        } else {
          delete validatedData.appData.table?.[tableId];
        }
      }

      return validatedData;
    },
  },
  [UserDataValidatorEnum.VALIDATE_CORRECT_LOCALE]: {
    id: UserDataValidatorEnum.VALIDATE_CORRECT_LOCALE,
    title: 'Validate if the correct locale is set',
    schemaVersion: 16, //When increment make sure this validator's logic is still relevant
    validate: (data) => {
      if (
        environment.global &&
        data.appData.locale === EnvironmentLocalesEnum.EN
      ) {
        return data;
      } else if (
        !environment.global &&
        data.appData.locale === EnvironmentLocalesEnum.RU
      ) {
        return data;
      } else {
        const validatedData = structuredClone(data);

        if (environment.global) {
          validatedData.appData.locale = EnvironmentLocalesEnum.EN;
        } else {
          validatedData.appData.locale = EnvironmentLocalesEnum.RU;
        }

        return validatedData;
      }
    },
  },
};

const validationOrder = [
  validators[UserDataValidatorEnum.VALIDATE_ALL_FIELDS_ARE_PRESENT],
  validators[UserDataValidatorEnum.VALIDATE_ALL_TABLES_HAVE_CORRECT_STRUCTURE],
  validators[UserDataValidatorEnum.VALIDATE_CORRECT_LOCALE],
];
